// eventEmitter.ts
const eventEmitter = new EventTarget();

export const emitEventNodeConfigurationEventListener = (
  eventName: string,
  detail?: any,
) => {
  eventEmitter.dispatchEvent(new CustomEvent(eventName, { detail }));
};

export const useNodeConfigurationEventListener = (
  eventName: string,
  callback: (detail: any) => void,
) => {
  useEffect(() => {
    const eventHandler = (event: Event) => {
      const customEvent = event as CustomEvent;
      callback(customEvent.detail);
    };

    eventEmitter.addEventListener(eventName, eventHandler);
    return () => {
      eventEmitter.removeEventListener(eventName, eventHandler);
    };
  }, [eventName, callback]);
};

export function useAutoSaveDebouncedCallback<T extends readonly unknown[]>(
  callback: () => void,
  delay: number,
  dependencies: T,
) {
  const hasMounted = useRef(false);
  useEffect(() => {
    // Prevent execution on the initial mount
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    const allRequiredFilled = dependencies.every(
      field =>
        field !== null &&
        field !== undefined &&
        (typeof field === 'string' ? field.trim() !== '' : true),
    );

    if (allRequiredFilled) {
      const handler = setTimeout(callback, delay);

      return () => clearTimeout(handler);
    }
  }, [...dependencies, delay]); // Dependencies trigger re-evaluation
}

export function checkPartialCompletionOfNodeConfig<
  T extends readonly unknown[],
>(callback: () => void, ...fields: T) {
  const filledFields = fields.filter(
    field =>
      field !== null &&
      field !== undefined &&
      (typeof field === 'string' ? field.trim() !== '' : true),
  );

  if (filledFields.length > 0 && filledFields.length < fields.length) {
    callback();
  }
}

// DEMO implementation of the above functions to show modal for unsaved changes
// useEffect(() => {
//     checkPartialCompletionOfNodeConfig(
//       () => {
//         setAllRequiredFieldsOfNodeConfigurationFilled(false);
//       },
//       communicationPlatformConfig,
//       aiPrompt,
//       toPhoneNumber,
//       llmModel,
//     );
//   }, [communicationPlatformConfig, aiPrompt, toPhoneNumber, llmModel]);

//   useNodeConfigurationEventListener('confirmSaveChanges', async detail => {
//     if (detail?.save) {
//       await saveConfigurations();
//     }
//   });
